// PoolScreener data — GeckoTerminal v2 client (public, no key; ~30 req/min).
// Resolves a DefiLlama-universe pool to its on-chain pool: address, current
// base-token price, total reserve (a tick-liquidity proxy), and the fee tier
// when GT reports it. The pool is found via the token→pools endpoint and
// matched on BOTH underlying tokens + the expected DEX so we never pick a
// same-token pool on the wrong venue.
import { z } from 'zod';
import { fetchJson } from './http';
import { cached } from './cache';
import type { PoolUniverseItem, ScreenerProject } from './types';

// GeckoTerminal's free tier is ~30 req/min, and we now make 2 calls per pool
// (token→pools + daily OHLCV). Serialize network calls ~2.2s apart (~27/min) so
// scans don't get throttled. Cache hits skip this, so re-scans stay instant.
const GECKO_MIN_INTERVAL_MS = 2200;
let geckoChain: Promise<void> = Promise.resolve();
function throttleGecko(): Promise<void> {
  const wait = geckoChain;
  geckoChain = wait.then(() => new Promise((r) => setTimeout(r, GECKO_MIN_INTERVAL_MS)));
  return wait;
}

// DefiLlama chain name → GeckoTerminal network slug.
const GT_NETWORK: Record<string, string> = {
  Base: 'base',
  Ethereum: 'eth',
  Arbitrum: 'arbitrum',
  Optimism: 'optimism',
  Polygon: 'polygon_pos',
};
export function gtNetwork(chain: string): string {
  return GT_NETWORK[chain] ?? chain.toLowerCase();
}

// GeckoTerminal DEX ids vary by network ("uniswap_v3", "uniswap-v3-base",
// "aerodrome-slipstream", …) — match a stable substring per project.
const GT_DEX_FRAGMENT: Record<ScreenerProject, string> = {
  'uniswap-v3': 'uniswap',
  'aerodrome-slipstream': 'aerodrome',
};

function num(s: string | null | undefined): number | null {
  if (s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// GT relationship id looks like "base_0xabc…"; keep only the address suffix.
function idAddr(id: string | null | undefined): string {
  if (!id) return '';
  const i = id.lastIndexOf('_');
  return (i >= 0 ? id.slice(i + 1) : id).toLowerCase();
}

const GtRel = z.object({ data: z.object({ id: z.string() }).nullable() }).partial();

const GtPoolSchema = z.object({
  id: z.string(),
  attributes: z
    .object({
      address: z.string(),
      base_token_price_usd: z.string().nullable().optional(),
      reserve_in_usd: z.string().nullable().optional(),
      pool_fee_percentage: z.string().nullable().optional(),
      volume_usd: z.object({ h24: z.string().nullable().optional() }).partial().optional(),
    })
    .passthrough(),
  relationships: z
    .object({ base_token: GtRel, quote_token: GtRel, dex: GtRel })
    .partial()
    .optional(),
});
export const GtTokenPoolsSchema = z.object({ data: z.array(GtPoolSchema) });
export type GtTokenPools = z.infer<typeof GtTokenPoolsSchema>;

export interface GeckoPoolDetail {
  poolAddress: string;
  /** Fraction. GT reports a percent string ("0.05" → 0.0005); null when absent. */
  feeTier: number | null;
  /** Total pool reserve in USD — the tick-liquidity proxy for Lambert IV. */
  reserveUsd: number | null;
  baseTokenPriceUsd: number | null;
  volumeUsd24h: number | null;
  dexId: string | null;
}

/** Pure: from a token's pools, pick the one matching BOTH underlying tokens
 *  and the expected DEX. Exposed for fixture tests. */
export function pickGeckoPool(
  resp: GtTokenPools,
  project: ScreenerProject,
  underlyingTokens: string[],
  /** DefiLlama entry's TVL — pick the pool whose reserve is CLOSEST to it, so a
   *  specific fee-tier entry doesn't collapse to the deepest pool of the pair.
   *  Omitted → highest-reserve (legacy). */
  targetTvlUsd?: number,
): GeckoPoolDetail | null {
  const want = new Set(underlyingTokens.map((t) => t.toLowerCase()));
  const dexFrag = GT_DEX_FRAGMENT[project];
  const matches = resp.data
    .filter((p) => {
      const b = idAddr(p.relationships?.base_token?.data?.id);
      const q = idAddr(p.relationships?.quote_token?.data?.id);
      const tokensMatch = want.size === 0 || (want.has(b) && want.has(q));
      const dex = (p.relationships?.dex?.data?.id ?? '').toLowerCase();
      return tokensMatch && dex.includes(dexFrag);
    })
    .map((p): GeckoPoolDetail => {
      const feePct = num(p.attributes.pool_fee_percentage);
      return {
        poolAddress: p.attributes.address,
        feeTier: feePct === null ? null : feePct / 100,
        reserveUsd: num(p.attributes.reserve_in_usd),
        baseTokenPriceUsd: num(p.attributes.base_token_price_usd),
        volumeUsd24h: num(p.attributes.volume_usd?.h24),
        dexId: p.relationships?.dex?.data?.id ?? null,
      };
    });
  if (matches.length === 0) return null;
  return targetTvlUsd === undefined
    ? matches.reduce((a, b) => ((b.reserveUsd ?? 0) > (a.reserveUsd ?? 0) ? b : a))
    : matches.reduce((a, b) =>
        Math.abs((b.reserveUsd ?? 0) - targetTvlUsd) < Math.abs((a.reserveUsd ?? 0) - targetTvlUsd)
          ? b
          : a,
      );
}

/** Resolve a universe pool's on-chain detail via GeckoTerminal (cached 1h).
 *  Returns null when the pool has no underlying tokens or no match is found. */
export async function fetchGeckoPoolDetail(
  pool: PoolUniverseItem,
): Promise<GeckoPoolDetail | null> {
  const tokens = pool.underlyingTokens;
  if (!tokens || tokens.length < 2) return null;
  const token0 = tokens[0];
  if (!token0) return null;
  const network = gtNetwork(pool.chain);
  const resp = await cached(`gecko:tokenpools:${network}:${token0.toLowerCase()}`, async () => {
    await throttleGecko();
    return fetchJson(
      `https://api.geckoterminal.com/api/v2/networks/${network}/tokens/${token0}/pools?page=1`,
      GtTokenPoolsSchema,
    );
  });
  return pickGeckoPool(resp, pool.project, tokens, pool.tvlUsd);
}

// ── daily OHLCV → multi-day average volume ───────────────────────────────
// Each row is [timestamp, open, high, low, close, volume]; volume is in USD.
const OhlcvSchema = z.object({
  data: z.object({
    attributes: z.object({ ohlcv_list: z.array(z.array(z.number())).default([]) }),
  }),
});

/** Pure: mean daily USD volume from an ohlcv_list (volume = index 5). Null when
 *  empty. Exposed for tests. */
export function avgDailyVolume(list: number[][]): number | null {
  const vols = list
    .map((row) => row[5])
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (vols.length === 0) return null;
  return vols.reduce((a, b) => a + b, 0) / vols.length;
}

/** Average daily USD volume over `days` from GeckoTerminal's pool OHLCV — smooths
 *  the noisy single-day figure. Cached 1h, throttled. Null on error/empty. */
export async function fetchGeckoDailyVolume(
  network: string,
  poolAddress: string,
  days = 90,
): Promise<number | null> {
  if (!poolAddress) return null;
  try {
    return await cached(`gecko:ohlcv:${network}:${poolAddress.toLowerCase()}:${days}`, async () => {
      await throttleGecko();
      const resp = await fetchJson(
        `https://api.geckoterminal.com/api/v2/networks/${network}/pools/${poolAddress}/ohlcv/day?limit=${days}&aggregate=1`,
        OhlcvSchema,
      );
      return avgDailyVolume(resp.data.attributes.ohlcv_list ?? []);
    });
  } catch {
    return null;
  }
}
